/* eslint-disable @typescript-eslint/no-unused-vars */

'use client';

import { use, useContext, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import * as Yup from 'yup';

import { CommonForm } from '@/components/CommonForm';
import { CommonHidden } from '@/components/CommonForm/CommonHidden';

import { useFormApi } from '@lamesarv-sdk/hooks';
import { phoneRegex } from '@lamesarv-sdk/tools';
import { useDynamicsForms } from '@/hooks/dynamicsForms';
import { useEnv } from '@/hooks/useEnv';
import { parsePhone } from '@/tools/dynamics';

import { DynamicsLeadType } from '@/types/dynamicsForms';
import { IComponentGenericInquiryField } from '@/types/cms';
import { IGenericInquiryFormProps } from './types';

import { StyleSimpleForm } from './Styles/Simple';
import { StyleClassicForm } from './Styles/Classic';
import { LayoutContext } from '@/contexts/LayoutContext';
import { InventoryLocations } from '@lamesarv-sdk/types';

interface IInventoryForm {
  leadType: string;
  leadSource: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  location: string;
  contact: string;
  product: string;
  comments: string;
  terms: boolean;
  subscribe: boolean;
}

// --

const ContactUsReset = ({ wasSuccessful }: { wasSuccessful: boolean }) => {
  const { reset } = useFormContext();

  useEffect(() => {
    if (!wasSuccessful) return;

    reset();
    reset({
      phone: '',
    });
  }, [wasSuccessful]);

  return null;
};

// --

const getYupStringSchema = (item: IComponentGenericInquiryField, label: string) => {
  return item.required ? Yup.string().required(`${label} is required`) : Yup.string().optional();
};

const mapFormFields = (fieldsBase: IComponentGenericInquiryField[], includeTerms?: boolean) => {
  const fields: { [field: string]: IComponentGenericInquiryField } = {};
  const fieldsSchema: Record<string, Yup.AnySchema> = {};

  const fieldMapping: Record<string, { key: string; label: string }> = {
    'first-name': { key: 'firstName', label: 'First Name' },
    'last-name': { key: 'lastName', label: 'Last Name' },
    comments: { key: 'comments', label: 'Comments' },
    'preferred-location': { key: 'location', label: 'Location' },
    'preferred-contact': { key: 'contact', label: 'Contact' },
    product: { key: 'product', label: 'Product' },
    email: { key: 'email', label: 'Email' },
    phone: { key: 'phone', label: 'Phone number' },
  };

  fieldsBase.forEach((item) => {
    fields[item.type] = item;

    const fieldMapped = fieldMapping[item.type];

    if (fieldMapped) {
      if (item.type === 'email') {
        fieldsSchema[fieldMapped.key] = item.required
          ? Yup.string().email('Invalid email').required(`${fieldMapped.label} is required`)
          : Yup.string().email('Invalid email').optional();
      } else if (item.type === 'phone') {
        fieldsSchema[fieldMapped.key] = item.required
          ? Yup.string()
              .required(`${fieldMapped.label} is required`)
              .matches(phoneRegex, 'Please enter a valid phone number.')
          : Yup.string().optional();
      } else {
        fieldsSchema[fieldMapped.key] = getYupStringSchema(item, fieldMapped.label);
      }
    } else {
      fieldsSchema[item.type] = Yup.mixed().optional();
    }
  });

  if (includeTerms) {
    fieldsSchema.terms = Yup.boolean().oneOf([true], 'You must accept the terms and conditions');
  }

  return {
    fields,
    schema: Yup.object().shape(fieldsSchema),
  };
};

const getLegacyZipCode = (location: string, inventoryLocations: InventoryLocations) => {
  const locationItem = Object.values(inventoryLocations).find((item) =>
    item.address.toLowerCase().includes(location.toLowerCase()),
  );

  let zipCode = locationItem?.address?.trim().split(' ').pop() || '';
  if (zipCode.length !== 5) zipCode = '';

  return zipCode;
};

const mapStyles = {
  simple: StyleSimpleForm,
  classic: StyleClassicForm,
};

export const GenericInquiryForm = (props: IGenericInquiryFormProps) => {
  const [_loadingLegacy, _submittedLegacy, _errorLegacy, sendFormLegacy] = useFormApi();
  const { loading, submitted, error, sendForm } = useDynamicsForms();
  const { context } = useContext(LayoutContext);

  const { env } = useEnv();

  const { fields, schema } = mapFormFields(props.fields, props.display_terms_checkbox);

  const wasSuccessful = !loading && !error && submitted;
  const wasError = !loading && error && submitted;

  const onSubmit = async (data: IInventoryForm) => {
    const formMappings = [
      {
        DataverseFieldName: 'lmrv_leadtype',
        DataverseFieldValue: data.leadType || props.custom_form_leadtype || String(DynamicsLeadType.buyer),
      },
      {
        DataverseFieldName: 'leadsourcecode',
        DataverseFieldValue: data.leadSource || props.custom_form_leadsource || 'lamesarv',
      },
      {
        DataverseFieldName: 'firstname',
        DataverseFieldValue: data.firstName || '',
      },
      {
        DataverseFieldName: 'lastname',
        DataverseFieldValue: data.lastName || '',
      },
      {
        DataverseFieldName: 'emailaddress1',
        DataverseFieldValue: data.email || '',
      },
      {
        DataverseFieldName: 'mobilephone',
        DataverseFieldValue: data.phone ? parsePhone(data.phone) : '',
      },
      {
        DataverseFieldName: 'lmrv_dealership',
        DataverseFieldValue: String(
          props.locationsOptionSet.find((item) => item.Label === data.location)?.DataverseValue ?? '',
        ),
      },
      {
        DataverseFieldName: 'preferredcontactmethodcode',
        DataverseFieldValue: data.contact || '',
      },
      {
        DataverseFieldName: 'lmrv_product',
        DataverseFieldValue: data.product || '',
      },
      {
        DataverseFieldName: 'lmrv_comments',
        DataverseFieldValue: data.comments || '',
      },
      {
        DataverseFieldName: 'lmrv_accepttermsandconditions',
        DataverseFieldValue: data.terms ? '1' : '0',
      },
      {
        DataverseFieldName: 'lmrv_subscribe',
        DataverseFieldValue: data.subscribe ? '1' : '0',
      },
    ];

    await sendForm({
      formId: `#${props.id || 'genericInquiryForm'}`,
      mappings: formMappings,
      dynamicsFormId: env.DYNAMICS_FORM_ID_INQUIRY || props.custom_dynamics_id,
    });

    if (env.ENABLE_FORMS_LEGACY) {
      sendFormLegacy(data);
      sendFormLegacy({
        ...data,
        zipCode: getLegacyZipCode(data.location, context.inventoryLocations),
        stockNumber: undefined,
        emailType: undefined,
        comments: `Preferred Location: ${data.location}\n${data.comments || ''}`,
      });
    }
  };

  useEffect(() => {
    if (wasSuccessful && props.onSuccess) {
      props.onSuccess();
    }
  }, [wasSuccessful]);

  const StyledForm = mapStyles[props.style] ?? mapStyles.simple;

  return (
    <CommonForm
      id={props.id || 'genericInquiryForm'}
      schema={schema}
      onSubmit={onSubmit}
      isDisabled={loading || wasSuccessful}
      tagGroup={props.tag_group || 'form'}
      tagItem={props.tag ? `form-inquiry-${props.tag}` : 'form-inquiry'}
      className="font-helvetica"
    >
      <CommonHidden name="formId" value={props.custom_active_campaign_id || '23'} />
      <CommonHidden name="formType" value={props.custom_form_type || 'more_info'} />
      <CommonHidden name="leadType" value={props.custom_form_leadtype || 'buyer'} />
      <CommonHidden name="leadSource" value={props.custom_form_leadsource || 'lamesarv'} />
      <StyledForm {...props} fieldsMapped={fields} wasError={wasError} wasSuccessful={wasSuccessful} />
      <ContactUsReset wasSuccessful={wasSuccessful} />
    </CommonForm>
  );
};

export const TailwindComponent = () => {
  return <div className="font-bold underline" />;
};
